import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as moment from 'moment-timezone';
import { DashboardService } from './dashboard/dashboard.service';
import { AlertService } from './alert.service';
import { StorageService } from './storage.service';
import {
  GatewayDashboardFilters,
  OverviewCardsResponse,
  DetailsTableResponse,
  FilterOptionsResponse,
} from '../dashboard/gateway-dashboard/gateway-summary/gateway-summary.data';
import { GatewayReportField } from '../dashboard/gateway-dashboard/gateway-summary/gateway-summary.data';
import { ReportTypes, ReportVersion} from '../reports/models/reports';

const GATEWAY_ROUTE = 'gateway_dashboard';

@Injectable()
export class GatewayDashboardService extends DashboardService {

  constructor(http: HttpClient, alertService: AlertService, storageService: StorageService) {
    super(http, alertService, storageService);
  }

  private getOverviewCards(params: object): Observable<OverviewCardsResponse> {
    return this.http.post<OverviewCardsResponse>(GATEWAY_ROUTE + '/overview_cards/', params);
  }

  private getFilteredReport(params: object, route: string): Observable<DetailsTableResponse> {
    return this.http.post<DetailsTableResponse>(GATEWAY_ROUTE + '/' + route + '/', params);
  }

  private getGatewayFilterOptions(params: object): Observable<FilterOptionsResponse> {
    return this.http.post<FilterOptionsResponse>(GATEWAY_ROUTE + '/filter_options/', params);
  }

  private getDownloadReportData(params: object): Observable<any> {
    return this.http.post<any>(GATEWAY_ROUTE + '/download_report/', params);
  }

  // ── Request builder ──────────────────────────────────────────────────────────

  buildRequest(filters: GatewayDashboardFilters, fields: string[], extra?: object): object {
    const timezone = this.getTimezone();
    const body: { [key: string]: any } = {
      report_version: ReportVersion.Clickhouse,
      report_type: ReportTypes.GATEWAY_TRANSACTION,
      date_range: {
        startDate: this.convertDateToUtc(filters.start_date, timezone),
        endDate: this.convertDateToUtc(filters.end_date, timezone),
      },
      timezone: timezone,
      accounts: this.getAccountIds(),
      fields,
      customer_mode: false,
      date_filter_type: 'session',
    };

    if (filters.compare_start_date && filters.compare_end_date) {
      body['compare_date_range'] = {
        startDate: this.convertDateToUtc(filters.compare_start_date, timezone),
        endDate: this.convertDateToUtc(filters.compare_end_date, timezone),
      };
    }

    const apiFilters: { [key: string]: any } = {};
    if (filters.merchant_id) {
      apiFilters['gateway_id'] = filters.merchant_id;
    }
    if (filters.campaign_id) {
      apiFilters['campaign_id'] = filters.campaign_id;
    }
    if (Object.keys(apiFilters).length) {
      body['filters'] = apiFilters;
    }

    if (filters.order_type) {
      body['stage'] = filters.order_type;
    }

    if (filters.count_type) {
      body['count_type'] = filters.count_type;
    }

    if (extra) {
      Object.assign(body, extra);
    }

    return body;
  }

  // ── Panel methods ────────────────────────────────────────────────────────────

  getKpiCards(filters: GatewayDashboardFilters): Observable<OverviewCardsResponse> {
    return this.getOverviewCards(this.buildRequest(filters, [GatewayReportField.DayOfWeek]));
  }

  getDetailsTable(filters: GatewayDashboardFilters, fields: string[], extra?: object): Observable<DetailsTableResponse> {
    return this.getFilteredReport(this.buildRequest(filters, fields, extra), 'details_table');
  }

  getFilterOptions(): Observable<FilterOptionsResponse> {
    const timezone = this.getTimezone();
    const startDate = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endDate = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const body = {
      report_version: ReportVersion.Clickhouse,
      report_type: ReportTypes.GATEWAY_TRANSACTION,
      date_range: {
        startDate: this.convertDateToUtc(startDate, timezone),
        endDate: this.convertDateToUtc(endDate, timezone),
      },
      timezone: timezone,
      accounts: this.getAccountIds(),
      fields: [GatewayReportField.DayOfWeek],
      customer_mode: false,
      date_filter_type: 'session',
    };
    return this.getGatewayFilterOptions(body);
  }

  downloadReport(filters: GatewayDashboardFilters, fields: string[]): Observable<any> {
    return this.getDownloadReportData(this.buildRequest(filters, fields));
  }

}
